import { useState } from 'react';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { DEFAULT_VISIT_PERIOD, type VisitFilters } from '../components/VisitFilterPanel';
import type { KanbanVisit } from '@pages/authenticated/agency-pages/visits/useVisitsKanban';

/**
 * Provides the feedback and cancel dialog state (open flags, target visit ids,
 * rating, observations, and reason) with their setters.
 *
 * @returns The dialog state values and setters.
 */
export function useFeedbackCancelDialogs() {
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackVisitId, setFeedbackVisitId] = useState<string | null>(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackObs, setFeedbackObs] = useState('');

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelVisitId, setCancelVisitId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  return {
    feedbackDialogOpen, setFeedbackDialogOpen,
    feedbackVisitId, setFeedbackVisitId,
    feedbackRating, setFeedbackRating,
    feedbackObs, setFeedbackObs,
    cancelDialogOpen, setCancelDialogOpen,
    cancelVisitId, setCancelVisitId,
    cancelReason, setCancelReason,
  };
}

/**
 * Resolves a member's display name from explicit name, first/last name, or email.
 *
 * @param m - The agency member record.
 * @returns The best available display name.
 */
function resolveMemberName(m: any): string {
  return m.name || [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
}

/**
 * Builds the human-readable chip labels for the active status and broker filters.
 *
 * @param filters - The current visit filters.
 * @param statusLabels - Map of status keys to localized labels.
 * @param agency - The agency whose members resolve broker display names.
 * @returns The list of active-filter chip labels.
 */
export function buildActiveFilterLabels(
  filters: VisitFilters,
  statusLabels: Record<string, string>,
  agency: any,
  periodOptions: Array<{ key: string; label: string }> = [],
): string[] {
  const statusPart = filters.statuses?.map((s) => statusLabels[s]) ?? [];
  const brokerPart = filters.brokers?.map((email) => {
    const m = agency?.members?.find((m: any) => m.email === email);
    return m ? resolveMemberName(m) : email;
  }) ?? [];
  const periodPart = isDefaultPeriod(filters.period)
    ? []
    : [periodOptions.find((o) => o.key === filters.period)?.label ?? filters.period!];
  return [...periodPart, ...statusPart, ...brokerPart];
}

/**
 * Creates a "new visit" handler that opens the drawer, or shows a permission
 * error when the user lacks the add-visit permission.
 *
 * @param canAddVisit - Whether the user may add a visit.
 * @param currentUserRole - The current user's role, used to skip the check for anonymous.
 * @param setEditingVisit - Setter to clear the editing visit.
 * @param setDrawerOpen - Setter to open the visit drawer.
 * @param t - The translation function scoped to the visits namespace.
 * @returns The click handler.
 */
export function createNewVisitHandler(
  canAddVisit: boolean,
  currentUserRole: string | undefined,
  setEditingVisit: (v: KanbanVisit | null) => void,
  setDrawerOpen: (v: boolean) => void,
  t: (key: string) => string,
) {
  return () => {
    if (!canAddVisit && currentUserRole && currentUserRole !== 'anonymus') {
      notifications.show({ color: 'red', message: t('visits.kanban.noPermission') });
      return;
    }
    setEditingVisit(null);
    setDrawerOpen(true);
  };
}

/**
 * Counts the active status and broker filters.
 *
 * @param filters - The current visit filters.
 * @returns The total number of active filters.
 */
export function countActiveFilters(filters: VisitFilters): number {
  const statuses = filters.statuses?.length ?? 0;
  const brokers = filters.brokers?.length ?? 0;
  return statuses + brokers + (isDefaultPeriod(filters.period) ? 0 : 1);
}

/**
 * Whether the period is the one the board opens on — the value that does not
 * narrow anything, so it earns neither a chip nor a place in the count.
 *
 * @param period - The period key currently applied, if any.
 * @returns True when the period is absent or the default.
 */
export function isDefaultPeriod(period: string | undefined): boolean {
  return !period || period === DEFAULT_VISIT_PERIOD;
}

type UpdateStatus = (
  id: string,
  status: 'done' | 'cancelled',
  extra: Record<string, unknown>,
) => Promise<void>;

type Dialogs = ReturnType<typeof useFeedbackCancelDialogs>;

/**
 * Builds the persisted extra fields for a completed visit, folding the star
 * rating and broker observation into the backend-supported `notes` field.
 *
 * @param rating - The 1-5 star rating (0 when unset).
 * @param obs - The broker's free-text observation.
 * @returns The extra fields to persist, or an empty object when there is nothing to record.
 */
export function buildFeedbackNotes(rating: number, obs: string): Record<string, unknown> {
  const parts: string[] = [];
  if (rating > 0) parts.push(`${'★'.repeat(rating)}${'☆'.repeat(Math.max(0, 5 - rating))} (${rating}/5)`);
  const trimmed = obs.trim();
  if (trimmed) parts.push(trimmed);
  return parts.length ? { notes: parts.join(' — ') } : {};
}

/**
 * Creates the feedback-dialog confirmation handler: marks the target visit
 * done with its rating and observations, then closes the dialog.
 *
 * @param updateStatus - Callback to persist the status change.
 * @param dialogs - The feedback/cancel dialog state and setters.
 * @returns The async confirmation handler.
 */
export function createConfirmFeedbackHandler(updateStatus: UpdateStatus, dialogs: Dialogs) {
  return async () => {
    if (!dialogs.feedbackVisitId) return;
    await updateStatus(dialogs.feedbackVisitId, 'done', buildFeedbackNotes(dialogs.feedbackRating, dialogs.feedbackObs));
    dialogs.setFeedbackDialogOpen(false);
    dialogs.setFeedbackVisitId(null);
  };
}

/**
 * Creates the cancel-dialog confirmation handler: marks the target visit
 * cancelled with its reason, then closes the dialog.
 *
 * @param updateStatus - Callback to persist the status change.
 * @param dialogs - The feedback/cancel dialog state and setters.
 * @returns The async confirmation handler.
 */
export function createConfirmCancelHandler(updateStatus: UpdateStatus, dialogs: Dialogs) {
  return async () => {
    if (!dialogs.cancelVisitId) return;
    const extra = dialogs.cancelReason.trim() ? { notes: dialogs.cancelReason.trim() } : {};
    await updateStatus(dialogs.cancelVisitId, 'cancelled', extra);
    dialogs.setCancelDialogOpen(false);
    dialogs.setCancelVisitId(null);
  };
}
